import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Button } from './button';
import { Eraser, Undo2, Trash2 } from 'lucide-react';

interface SignatureCanvasProps {
  width?: number;
  height?: number;
  onSignatureChange?: (signatureData: string | null) => void;
  initialSignature?: string | null;
  className?: string;
}

interface Point {
  x: number;
  y: number;
}

interface Stroke {
  points: Point[];
  color: string;
  width: number;
}

export const SignatureCanvas: React.FC<SignatureCanvasProps> = ({
  width = 400,
  height = 200,
  onSignatureChange,
  initialSignature,
  className = ""
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [strokes, setStrokes] = useState<Stroke[]>([]);
  const [currentStroke, setCurrentStroke] = useState<Point[]>([]);
  const [strokeHistory, setStrokeHistory] = useState<Stroke[]>([]);
  const [currentColor] = useState('#000000');
  const [currentWidth] = useState(2);
  const [lastPoint, setLastPoint] = useState<Point | null>(null);

  // Load initial signature if provided
  useEffect(() => {
    if (initialSignature && canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        const img = new Image();
        img.onload = () => {
          ctx.clearRect(0, 0, width, height);
          ctx.drawImage(img, 0, 0, width, height);
        };
        img.src = initialSignature;
      }
    }
  }, [initialSignature, width, height]);

  // Redraw canvas when strokes change
  useEffect(() => {
    if (canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        // Enable smooth rendering
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        
        ctx.clearRect(0, 0, width, height);
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, width, height);
        
        strokes.forEach(stroke => {
          if (stroke.points.length > 1) {
            ctx.beginPath();
            ctx.strokeStyle = stroke.color;
            ctx.lineWidth = stroke.width;
            ctx.lineCap = 'round';
            ctx.lineJoin = 'round';
            
            ctx.moveTo(stroke.points[0].x, stroke.points[0].y);
            for (let i = 1; i < stroke.points.length; i++) {
              ctx.lineTo(stroke.points[i].x, stroke.points[i].y);
            }
            ctx.stroke();
          }
        });
      }
    }
  }, [strokes, width, height]);


  const getPointFromEvent = useCallback((e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>): Point => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };

    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    let clientX: number, clientY: number;

    if ('touches' in e) {
      // Touch event
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      // Mouse event
      clientX = e.clientX;
      clientY = e.clientY;
    }

    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY
    };
  }, []);

  const drawLine = useCallback((from: Point, to: Point, color: string, width: number) => {
    if (canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        // Enable smooth line rendering
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        
        ctx.beginPath();
        ctx.strokeStyle = color;
        ctx.lineWidth = width;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.moveTo(from.x, from.y);
        ctx.lineTo(to.x, to.y);
        ctx.stroke();
      }
    }
  }, []);

  const startDrawing = useCallback((e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    setIsDrawing(true);
    const point = getPointFromEvent(e);
    setCurrentStroke([point]);
    setLastPoint(point);
    
    // Draw the first point immediately
    if (canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.beginPath();
        ctx.fillStyle = currentColor;
        ctx.arc(point.x, point.y, currentWidth / 2, 0, 2 * Math.PI);
        ctx.fill();
      }
    }
  }, [getPointFromEvent, currentColor, currentWidth]);

  const draw = useCallback((e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if (!isDrawing || !lastPoint) return;
    
    e.preventDefault();
    const point = getPointFromEvent(e);
    
    // Draw line immediately for smooth real-time feedback
    drawLine(lastPoint, point, currentColor, currentWidth);
    
    setCurrentStroke(prev => [...prev, point]);
    setLastPoint(point);
  }, [isDrawing, lastPoint, getPointFromEvent, currentColor, currentWidth, drawLine]);

  const stopDrawing = useCallback(() => {
    if (!isDrawing) return;
    
    setIsDrawing(false);
    setLastPoint(null);
    
    if (currentStroke.length > 0) {
      const newStroke: Stroke = {
        points: currentStroke,
        color: currentColor,
        width: currentWidth
      };
      
      setStrokes(prev => {
        const newStrokes = [...prev, newStroke];
        setStrokeHistory(prev => [...prev, newStroke]);
        return newStrokes;
      });
      setCurrentStroke([]);
    }
  }, [isDrawing, currentStroke, currentColor, currentWidth]);

  const clearCanvas = useCallback(() => {
    setStrokes([]);
    setCurrentStroke([]);
    setStrokeHistory([]);
    setLastPoint(null);
    setIsDrawing(false);
    if (canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.clearRect(0, 0, width, height);
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, width, height);
      }
    }
    onSignatureChange?.(null);
  }, [width, height, onSignatureChange]);

  const undoLastStroke = useCallback(() => {
    if (strokes.length > 0) {
      setStrokes(prev => prev.slice(0, -1));
      setStrokeHistory(prev => prev.slice(0, -1));
    }
  }, [strokes.length]);

  const getSignatureData = useCallback(() => {
    if (canvasRef.current) {
      return canvasRef.current.toDataURL('image/png');
    }
    return null;
  }, []);

  // Notify parent when signature changes
  useEffect(() => {
    if (strokes.length > 0) {
      const signatureData = getSignatureData();
      onSignatureChange?.(signatureData);
    }
  }, [strokes, getSignatureData, onSignatureChange]);

  return (
    <div className={`flex flex-col space-y-4 ${className}`}>
      <div className="flex justify-between items-center">
        <h4 className="text-sm font-medium">Draw Signature</h4>
        <div className="flex space-x-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={undoLastStroke}
            disabled={strokes.length === 0}
            className="h-8 px-2"
          >
            <Undo2 className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={clearCanvas}
            className="h-8 px-2"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
      
      <div className="border-2 border-dashed border-gray-300 rounded-lg overflow-hidden">
        <canvas
          ref={canvasRef}
          width={width}
          height={height}
          className="block cursor-crosshair touch-none select-none"
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          onTouchStart={(e) => {
            e.preventDefault();
            startDrawing(e);
          }}
          onTouchMove={(e) => {
            e.preventDefault();
            draw(e);
          }}
          onTouchEnd={(e) => {
            e.preventDefault();
            stopDrawing();
          }}
          onTouchCancel={(e) => {
            e.preventDefault();
            stopDrawing();
          }}
        />
      </div>
      
      <p className="text-xs text-gray-500 text-center">
        Draw your signature above. Works with mouse, touch, or stylus.
      </p>
    </div>
  );
};
